export interface StorageStatus {
  /** Bytes the browser says this origin is currently using. */
  usageBytes: number | null;
  /** Bytes the browser is willing to give this origin. */
  quotaBytes: number | null;
  /**
   * True when the origin is in persistent mode. Persistent origins are skipped
   * during automatic eviction, which is what stops iOS deleting a 200 MB model
   * after a week of not using the app.
   */
  persisted: boolean;
  /** True when the page is cross-origin isolated, so multi-threaded WASM works. */
  crossOriginIsolated: boolean;
  /** True when running as an installed home-screen app rather than a browser tab. */
  standalone: boolean;
}

export async function getStorageStatus(): Promise<StorageStatus> {
  let usageBytes: number | null = null;
  let quotaBytes: number | null = null;
  let persisted = false;

  try {
    if (navigator.storage?.estimate) {
      const est = await navigator.storage.estimate();
      usageBytes = est.usage ?? null;
      quotaBytes = est.quota ?? null;
    }
  } catch {
    /* Safari can refuse; unknown beats wrong. */
  }

  try {
    persisted = (await navigator.storage?.persisted?.()) ?? false;
  } catch {
    persisted = false;
  }

  return {
    usageBytes,
    quotaBytes,
    persisted,
    crossOriginIsolated: Boolean((globalThis as any).crossOriginIsolated),
    standalone: isStandalone(),
  };
}

/**
 * What is actually sitting in Cache Storage, per cache.
 *
 * This exists because "the app did not start" and "the app shell is incomplete"
 * are the same event seen from two sides, and nothing in the report could tell
 * them apart. The shell is the only thing in here; a count well below what the
 * build produced, or a service worker that never took control, says the
 * precache did not survive — which on iOS is the difference between a bug in
 * the app and WebKit refusing the storage.
 */
export async function getShellCacheReport(): Promise<string[]> {
  const lines: string[] = [];
  try {
    if (!('caches' in globalThis)) return ['Cache Storage: unavailable'];

    const keys = await caches.keys();
    if (keys.length === 0) {
      lines.push('Cache Storage: EMPTY, the service worker has not precached the app shell.');
    }

    for (const key of keys) {
      const cache = await caches.open(key);
      const requests = await cache.keys();
      let bytes = 0;
      let unknown = 0;
      for (const req of requests) {
        const res = await cache.match(req);
        // `Content-Length` is absent on opaque and some SW-synthesised
        // responses. Counting what is knowable beats reporting a wrong total.
        const len = Number(res?.headers.get('content-length'));
        if (Number.isFinite(len) && len > 0) bytes += len;
        else unknown += 1;
      }
      lines.push(
        `Cache "${key}": ${requests.length} entries, ${(bytes / 1048576).toFixed(1)} MB` +
          (unknown ? ` (+${unknown} of unknown size)` : '')
      );
    }

    const controlled = Boolean(navigator.serviceWorker?.controller);
    lines.push(`Service worker: ${controlled ? 'controlling this page' : 'NOT controlling'}`);
  } catch (e: any) {
    lines.push(`Cache Storage: could not be read, ${e?.message ?? e}`);
  }
  return lines;
}

export function isStandalone(): boolean {
  const iosStandalone = (navigator as any).standalone === true;
  const displayMode =
    typeof matchMedia === 'function' && matchMedia('(display-mode: standalone)').matches;
  return Boolean(iosStandalone || displayMode);
}

/**
 * Ask the browser to keep this origin's data.
 *
 * Without it, WebKit deletes script-created storage after seven days without
 * user interaction — which is exactly the shape of "download the pack, don't
 * open the app for two weeks, then land in Moscow". Safari is more willing to
 * grant this to an installed home-screen app, which is why the setup flow
 * pushes for installation before download.
 */
export async function requestPersistentStorage(): Promise<boolean> {
  try {
    if (!navigator.storage?.persist) return false;
    if (await navigator.storage.persisted()) return true;
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}

export function formatBytes(b: number | null | undefined): string {
  if (b === null || b === undefined) return 'unknown';
  if (b >= 1024 ** 3) return `${(b / 1024 ** 3).toFixed(2)} GB`;
  if (b >= 1024 ** 2) return `${(b / 1024 ** 2).toFixed(1)} MB`;
  return `${(b / 1024).toFixed(0)} KB`;
}
